import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { DefaultCatchBoundary } from "./components/DefaultCatchBoundary";
import { NotFound } from "./components/NotFound";
import { QueryClient } from "@tanstack/react-query";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import * as Sentry from "@sentry/tanstackstart-react";
import { DEFAULT_LOCALE } from "~/lib/utils/with-locale";
import { isSentryEnabledClient } from "~/lib/sentry/enabled-client";

export function getRouter() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Handle abort errors gracefully - they're expected during navigation/HMR
        retry: (failureCount, error) => {
          // Don't retry on abort errors
          if (
            error instanceof Error &&
            (error.name === "AbortError" || error.message.includes("aborted"))
          ) {
            return false;
          }
          // Retry other errors up to 3 times
          return failureCount < 3;
        },
        // Reduce stale time to avoid unnecessary refetches
        staleTime: 0,
        // Keep queries in cache for a reasonable time
        gcTime: 5 * 60 * 1000, // 5 minutes
      },
    },
  });

  const router = createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    defaultViewTransition: false,
    defaultErrorComponent: DefaultCatchBoundary,
    defaultNotFoundComponent: () => <NotFound />,
    notFoundMode: "root", // Always handle not-found at root level
    rewrite: {
      input: ({ url }) => {
        // On admin subdomain: /de/sign-up (locale-prefixed URL) -> /advertiser/sign-up (route path)
        // so that the route tree matches. The "advertiser" segment is internal and not visible in the URL.
        if (url.hostname.startsWith(import.meta.env.VITE_ADMIN_HOST)) {
          // Strip locale prefix so /de/sign-up -> /sign-up, then add /advertiser for route matching
          const pathWithoutLocale = url.pathname.replace(
            new RegExp(`^/${DEFAULT_LOCALE}(/|$)`),
            "$1"
          ) || "/";
          url.pathname = `/advertiser${pathWithoutLocale}`;
          return url;
        }
        return undefined;
      },
      output: ({ url }) => {
        // Reverse: /advertiser/sign-up -> /de/sign-up in the browser when on admin subdomain
        let hostname: string | null = null;
        let isAdminSubdomain = false;

        if (url.hostname) {
          hostname = url.hostname;
          isAdminSubdomain = hostname.startsWith(
            import.meta.env.VITE_ADMIN_HOST
          );
        } else if (typeof window !== "undefined") {
          hostname = window.location.hostname;
          isAdminSubdomain = hostname.startsWith(
            import.meta.env.VITE_ADMIN_HOST
          );
        } else {
          return undefined;
        }

        if (isAdminSubdomain && url.pathname.startsWith("/advertiser")) {
          // Replace /advertiser with /de so the URL shows the locale (e.g. /de/sign-up)
          url.pathname = url.pathname.replace(/^\/advertiser/, `/${DEFAULT_LOCALE}`);
          return url;
        }
        return undefined;
      },
    },
  });

  // Initialize Sentry on client-side only, and only when not in development
  // Use MODE instead of PROD/DEV as they are build-time constants that may not reflect runtime
  const isDevelopment = import.meta.env.MODE === "development";
  const shouldInitSentry =
    !router.isServer && !isDevelopment && isSentryEnabledClient();

  if (shouldInitSentry) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,

      // Adds request headers and IP for users, for more info visit:
      // https://docs.sentry.io/platforms/javascript/guides/tanstackstart-react/configuration/options/#sendDefaultPii
      sendDefaultPii: false,

      // Filter out expected authentication errors (wrong password, user not found, etc.)
      // These are user errors that shouldn't spam error logs
      beforeSend(event, hint) {
        const error = hint.originalException;
        // Ignore ExpectedAuthError - these are expected user errors
        if (
          error &&
          typeof error === "object" &&
          "name" in error &&
          error.name === "ExpectedAuthError"
        ) {
          return null; // Don't send to Sentry
        }
        return event;
      },

      // Enable Session Replay
      integrations: [
        Sentry.replayIntegration({
          // Mask all text content and user input by default to protect PII
          maskAllText: true,
          blockAllMedia: false,
        }),
      ],

      // Session Replay sample rates
      // sessionSampleRate: percentage of all user sessions to record (0.0 to 1.0)
      // errorSampleRate: percentage of sessions with errors to record (0.0 to 1.0)
      replaysSessionSampleRate: 0.1, // Record 10% of all sessions
      replaysOnErrorSampleRate: 1.0, // Record 100% of sessions with errors
    });
  }

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
  });
  return router;
}
